// Mock domain data for the Rangeet Family App prototype, derived from the SoftBD brief.

export type Umbrella = 'Self' | 'Society' | 'Ecology'

export interface Activity {
  id: string
  title: string
  writeUp: string
  umbrella: Umbrella
  module: string
  ageGroup: string
  duration: number // minutes
  tags: string[]
  locked: boolean
}

export interface Playlist {
  id: string
  name: string
  category: Umbrella
  color: string
  activityIds: string[]
  saved: boolean
}

export interface LicencePolicy {
  id: string
  name: string
  price: number
  period: string
  perks: string[]
  recommended?: boolean
}

export const ACTIVITIES: Activity[] = [
  {
    id: 'a1',
    title: 'Feelings Weather Report',
    writeUp:
      'A gentle daily ritual where children describe how they feel using weather words — sunny, cloudy, stormy — building emotional vocabulary.',
    umbrella: 'Self',
    module: 'Emotions',
    ageGroup: '4-6',
    duration: 10,
    tags: ['emotions', 'talking'],
    locked: false,
  },
  {
    id: 'a2',
    title: 'My Superpower Cape',
    writeUp:
      'Kids identify one thing they are proud of and craft a paper cape that celebrates it, boosting self-esteem.',
    umbrella: 'Self',
    module: 'Confidence',
    ageGroup: '5-7',
    duration: 20,
    tags: ['craft', 'confidence'],
    locked: false,
  },
  {
    id: 'a3',
    title: 'Calm-Down Bottle',
    writeUp:
      'A sensory glitter bottle activity that teaches self-regulation and mindful breathing.',
    umbrella: 'Self',
    module: 'Regulation',
    ageGroup: '4-8',
    duration: 15,
    tags: ['mindfulness'],
    locked: true,
  },
  {
    id: 'a4',
    title: 'Kindness Coupons',
    writeUp:
      'Children make coupons for helpful acts and gift them to family members, practising empathy.',
    umbrella: 'Society',
    module: 'Empathy',
    ageGroup: '5-8',
    duration: 20,
    tags: ['family', 'kindness'],
    locked: false,
  },
  {
    id: 'a5',
    title: 'Our Neighbourhood Map',
    writeUp:
      'Draw a map of the people and places that help our community — the doctor, the shopkeeper, the teacher.',
    umbrella: 'Society',
    module: 'Community',
    ageGroup: '6-8',
    duration: 25,
    tags: ['drawing', 'community'],
    locked: true,
  },
  {
    id: 'a6',
    title: 'Sharing Circle',
    writeUp:
      'A structured turn-taking game that builds patience and listening among siblings and friends.',
    umbrella: 'Society',
    module: 'Cooperation',
    ageGroup: '4-7',
    duration: 15,
    tags: ['group', 'talking'],
    locked: false,
  },
  {
    id: 'a7',
    title: 'Seed to Sprout',
    writeUp:
      'Plant a bean in a jar and track its growth over a week, nurturing responsibility and wonder for nature.',
    umbrella: 'Ecology',
    module: 'Growing',
    ageGroup: '4-8',
    duration: 30,
    tags: ['nature', 'science'],
    locked: false,
  },
  {
    id: 'a8',
    title: 'Waste Sorting Hunt',
    writeUp:
      'A playful hunt around the home to sort recyclables from waste, teaching sustainable habits.',
    umbrella: 'Ecology',
    module: 'Sustainability',
    ageGroup: '5-8',
    duration: 20,
    tags: ['recycling'],
    locked: true,
  },
  {
    id: 'a9',
    title: 'Cloud Story Time',
    writeUp:
      'Lie back, watch the clouds and invent a story together — sparking imagination and calm.',
    umbrella: 'Ecology',
    module: 'Wonder',
    ageGroup: '4-6',
    duration: 15,
    tags: ['imagination'],
    locked: false,
  },
]

export const PLAYLISTS: Playlist[] = [
  {
    id: 'p1',
    name: 'Big Feelings, Little People',
    category: 'Self',
    color: '#C08497',
    activityIds: ['a1', 'a2', 'a3'],
    saved: true,
  },
  {
    id: 'p2',
    name: 'Kind Hearts at Home',
    category: 'Society',
    color: '#8E7CA3',
    activityIds: ['a4', 'a5', 'a6'],
    saved: false,
  },
  {
    id: 'p3',
    name: 'Little Nature Explorers',
    category: 'Ecology',
    color: '#6E8B6E',
    activityIds: ['a7', 'a8', 'a9'],
    saved: true,
  },
  {
    id: 'p4',
    name: 'Bedtime Calm Rituals',
    category: 'Self',
    color: '#C08497',
    activityIds: ['a3', 'a9'],
    saved: false,
  },
]

export const LICENCE_POLICIES: LicencePolicy[] = [
  {
    id: 'lic_monthly',
    name: 'Monthly Family Pass',
    price: 149,
    period: '/ month',
    perks: ['Unlock all activities', 'New playlists weekly', 'Cancel anytime'],
  },
  {
    id: 'lic_yearly',
    name: 'Yearly Family Pass',
    price: 1199,
    period: '/ year',
    perks: [
      'Everything in Monthly',
      'Save 33% vs monthly',
      'Priority new content',
      'Offline downloads',
    ],
    recommended: true,
  },
  {
    id: 'lic_single',
    name: 'Single Umbrella',
    price: 79,
    period: 'one-time',
    perks: ['Unlock one umbrella', 'Lifetime access', 'Great to try things out'],
  },
]

export const activityById = (id: string) => ACTIVITIES.find((a) => a.id === id)
